import { useEffect } from 'react';
import type { FormInstance } from 'antd';
import type { AdminProductFormValues } from '../../interfaces';

type UseCreateFormSizeSyncArgs = {
  filteredSizeIds: string[];
  filteredSizeKey: string;
  form: FormInstance<AdminProductFormValues>;
  selectedTypeRequiresSizes: boolean;
};

const createVariantStocksForSizes = (
  sizeIds: string[],
  currentVariantStocks: Record<string, unknown> = {},
  fallbackStock: unknown
) =>
  sizeIds.reduce<Record<string, number>>((acc, sizeId) => {
    const currentStock = Number(currentVariantStocks[sizeId]);
    const fallback = Number(fallbackStock);

    acc[sizeId] = Number.isFinite(currentStock)
      ? Math.max(0, currentStock)
      : Number.isFinite(fallback)
        ? Math.max(0, fallback)
        : 0;

    return acc;
  }, {});

export const useCreateFormSizeSync = ({
  filteredSizeIds,
  filteredSizeKey,
  form,
  selectedTypeRequiresSizes,
}: UseCreateFormSizeSyncArgs) => {
  useEffect(() => {
    const allowedSizeIds = new Set(filteredSizeIds);
    const materials = form.getFieldValue('materials') || [];
    const nextDefaultSizeIds = selectedTypeRequiresSizes && filteredSizeIds.length
      ? ((form.getFieldValue('sizeIds') || []) as string[]).filter((id) => allowedSizeIds.has(id))
      : [];

    form.setFieldsValue({
      sizeIds: nextDefaultSizeIds.length ? nextDefaultSizeIds : filteredSizeIds,
      materials: materials.map((material: Record<string, unknown>) => {
        const currentSizeIds = Array.isArray(material.sizeIds)
          ? material.sizeIds.filter((id: string) => allowedSizeIds.has(id))
          : [];
        const syncedSizeIds = currentSizeIds.length ? currentSizeIds : filteredSizeIds;

        return {
          ...material,
          sizeIds: selectedTypeRequiresSizes ? syncedSizeIds : [],
          variantStocks: selectedTypeRequiresSizes
            ? createVariantStocksForSizes(
              syncedSizeIds,
              material.variantStocks as Record<string, unknown>,
              material.stockQuantity
            )
            : {},
        };
      }),
    });
  }, [filteredSizeKey, form, filteredSizeIds, selectedTypeRequiresSizes]);
};
